import React, { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import Hummingbird from "../components/brand/Hummingbird";
import SEO from "../components/SEO";

const AuthForm = ({ mode = "login" }) => {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/academy/dashboard";

  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "register") {
        const u = await register(email, pw, name);
        nav(u.role === "admin" ? "/admin" : redirect);
      } else {
        const u = await login(email, pw);
        nav(u.role === "admin" ? "/admin" : redirect);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center" data-testid={`auth-${mode}-page`}>
      <SEO
        title={mode === "register" ? "Konto erstellen" : "Anmelden"}
        description={mode === "register"
          ? "Erstelle dein persönliches SAT-Konto und tritt in den geschützten Academy-Bereich ein."
          : "Melde dich in deinem SAT-Konto an und setze deine Reise fort."}
        path={mode === "register" ? "/auth/register" : "/auth/login"}
        noindex
      />
      <div className="w-full max-w-[1100px] grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center">
        {/* Left: poetic side panel — desktop only */}
        <div className="hidden lg:block">
          <div className="relative">
            <div className="absolute -top-10 -left-6 opacity-20 pointer-events-none">
              <Hummingbird size={180} />
            </div>
            <div className="relative">
              <div className="overline mb-5">SAT · Schweizer Akademie</div>
              <h2 className="serif text-4xl xl:text-5xl font-light leading-tight">
                {mode === "register"
                  ? <>Tritt ein in einen<br/><span className="italic text-[#D4AF37]">stillen Spiegel.</span></>
                  : <>Willkommen<br/><span className="italic text-[#D4AF37]">zurück.</span></>}
              </h2>
              <p className="serif italic text-white/55 text-lg mt-6 max-w-md leading-relaxed">
                {mode === "register"
                  ? "Hier beginnt deine Reise — durch zwölf Räume, eine Lebenslandkarte, und Module, die nur dich meinen."
                  : "Dein Bereich, deine Antworten, dein Weg — genau dort, wo du ihn zuletzt verlassen hast."}
              </p>
              <div className="mt-10 flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-white/40">
                <span className="w-10 h-px bg-[#D4AF37]/40"></span>
                <span>Sicher · DSGVO · CH-Hosting</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form card */}
        <div className="max-w-md w-full mx-auto lg:mx-0">
          <div className="text-center lg:text-left mb-8">
            <div className="lg:hidden">
              <Hummingbird size={56} />
            </div>
            <div className="overline mt-4 lg:mt-0">SAT · {mode === "register" ? "Konto erstellen" : "Anmelden"}</div>
            <h1 className="headline-xl text-4xl mt-2">{mode === "register" ? "Willkommen" : "Login"}</h1>
          </div>
          <form onSubmit={submit} className="glass rounded-2xl p-8 space-y-4" data-testid={`auth-${mode}-form`}>
            {mode === "register" && (
              <input className="sat-input" placeholder="Vorname" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name" />
            )}
            <input type="email" required className="sat-input" placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email" />
            <input type="password" required minLength={6} className="sat-input" placeholder="Passwort" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="auth-password" />
            <button disabled={loading} className="btn-primary w-full justify-center" data-testid={`auth-${mode}-submit`}>
              {loading ? <Loader2 className="animate-spin" size={16} /> : (mode === "register" ? <>Konto erstellen <ArrowRight size={16} /></> : <>Anmelden <ArrowRight size={16} /></>)}
            </button>
            {err && <p className="text-red-300 text-sm">{err}</p>}
            <div className="text-center text-sm text-white/55 pt-2">
              {mode === "login" ? (
                <>Noch kein Konto? <Link to={`/auth/register?redirect=${encodeURIComponent(redirect)}`} className="text-[#D4AF37] hover:underline">Registrieren</Link></>
              ) : (
                <>Bereits Mitglied? <Link to={`/auth/login?redirect=${encodeURIComponent(redirect)}`} className="text-[#D4AF37] hover:underline">Anmelden</Link></>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
